import React, { useState, useCallback } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const CourseImportPopup = ({ onUpload, onClose }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (selectedFile) => {
    // Check file type (Excel files)
    const validExtensions = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // Check file extension from name as fallback
    const validExtensionsNames = ['.xlsx', '.xls'];
    const fileName = selectedFile.name.toLowerCase();
    const isExtensionValid = validExtensionsNames.some(ext => fileName.endsWith(ext));

    if (!validExtensions.includes(selectedFile.type) && !isExtensionValid) {
      setError("Please upload a valid Excel file (.xlsx, .xls)");
      return false;
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      setError("Please upload a file less than 10MB");
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileProcessing = async (selectedFile) => {
    
    setFile(null);
    setError(null);

    if (!validateFile(selectedFile)) {
      return;
    }

    const { isValid, error } = await validateExcelContent(selectedFile);
    if (!isValid) {
      setError(error);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];
      setFile({
        name: selectedFile.name,
        size: selectedFile.size,
        base64url: base64String,
      });
      setError(null);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      handleFileProcessing(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      handleFileProcessing(selectedFile);
    }
  };

  const handleUpload = () => {
    if (file) {
      onUpload({ base64url: file.base64url });
      setFile(null);
      onClose();
    } else {
      setError("Please select a file to upload.");
    }
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  const validateExcelContent = async (file) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        return { isValid: false, error: "The Excel file has no worksheets" };
      }
  
      // Check required columns
      const requiredColumns = [
        "S.No", 
        "Certificate Name", 
        "Provider", 
        "Cost(In $ or Rs)", 
        "Practice", 
        "Materials Link"
      ];
      
      const actualColumns = [];
      worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
        actualColumns.push(cell.text);
      });
  
      if (actualColumns.length < requiredColumns.length) {
        return { 
          isValid: false, 
          error: "Upload Failed: Column structure doesn't match the template" 
        };
      }
  
      // Check if any data exists beyond header
      if (worksheet.rowCount <= 1) {
        return { 
          isValid: false, 
          error: "Upload Failed: The Excel file contains no data" 
        };
      }
  
      return { isValid: true };
    } catch (error) {
      return { 
        isValid: false, 
        error: "Upload Failed: Error processing the Excel file" 
      };
    }
  };

  const handleDownloadTemplate = async () => {
    const headers = [
      { label: "S.No", key: "sno" },
      { label: "Certificate Name", key: "certificateName" },
      { label: "Provider", key: "provider" },
      { label: "Cost(In $ or Rs)", key: "cost" },
      { label: "Practice", key: "practice" },
      { label: "Materials Link", key: "materialsLink" },
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Template");

    worksheet.columns = headers.map((header) => ({
      header: header.label,
      key: header.key,
      width: 25,
    }));

    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "Certifications_Template.xlsx");
  };

  return (
    <div
      className="modal fade show"
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)'
      }}

    >
      <div className="modal-dialog certification-modal approval-modal-width">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title">Import Certifications</h6>
            <button
              type="button"
              className="btn-close font-12"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            <div className="row">
              {!file && (
                <div className="col-md-12 mb-3">
                  <label className="font-12 mb-1 regular-text grey-text d-block">
                    Upload Certifications List
                  </label>
                  <div
                    className={`upload-file-modal px-4 py-2 w-100 mb-2 btn medium-text ${isDragging ? "drag-over" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <label
                      htmlFor="fileUpload"
                      className="d-block text-center cursor-pointer"
                    >
                      <img
                        src="/Images/popup-upload.svg"
                        alt="upload-icon"
                        className="my-3"
                      />
                      <span className="d-block">
                        Drag or Browse to choose file
                      </span>
                    </label>

                    <div className="d-flex align-items-center justify-content-center grey-text font-10 my-1">
                      Use this template to enter details
                      <a
                        className="d-block text-center mx-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadTemplate();
                        }}
                      >
                        <img
                          className="me-1"
                          src="/Images/excel-icon.svg"
                          alt="excel-icon"
                        />
                        Download Template
                      </a>
                    </div>

                    <input
                      id="fileUpload"
                      type="file"
                      className="d-none"
                      onChange={handleFileChange}
                      accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    />
                  </div>
                  {error  && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        <img
                          src="/Images/error-icon.svg"
                          alt="error-icon"
                          className="me-2"
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span className="text-danger font-12">{error}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {file && (
                <div className="col-md-7 mb-3">
                  <label className="font-12 mb-1 regular-text grey-text d-block">
                    Uploaded File
                  </label>
                  <div className={`d-flex align-items-center bg-white p-2 border border-light-subtle rounded-3 shadow-sm text-decoration-none text-dark ${error ? "border-danger" : "border-light-subtle"
                    }`}>
                    <img
                      src="/Images/excel-icon.svg"
                      alt="excel-icon"
                      className="me-2 width-24"
                    />
                    <div>
                      <p className="medium-text font-12">{file.name}</p>
                      <p className="regular-text font-10 grey-text">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-close ms-auto me-1 font-11" aria-label="Close"
                      onClick={removeFile}
                    ></button>
                  </div>

                  {error && file && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        <img
                          src="/Images/error-icon.svg"
                          alt="error-icon"
                          className="me-2"
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span className="text-danger font-12">{error}</span>
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
          <div className="modal-footer border-0">
            <div className="col-md-12 d-flex justify-content-end">
              <button className="btn hive-btn-secondary me-3" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn hive-btn-primary"
                onClick={handleUpload}
                disabled={!!error || !file}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseImportPopup;